<template>
  <div class="condition-form">
    <el-form ref="conditionForm" :model="form" :rules="formRules" label-width="100px">
      <!-- 常用条件快捷模板（仅新建时显示）：运营 90% 建的就是这几条，点一下预填规则 -->
      <el-form-item v-if="!isEdit" :label="$t('autoPush.condTplLabel')">
        <el-button
          v-for="tpl in quickTemplates"
          :key="tpl.key"
          size="mini"
          class="tpl-btn"
          @click="applyTemplate(tpl)"
        >{{ $t('autoPush.' + tpl.labelKey) }}</el-button>
        <div class="form-tip">{{ $t('autoPush.condTplTip') }}</div>
      </el-form-item>

      <el-form-item :label="$t('autoPush.condLabelName')" prop="name">
        <el-input v-model="form.name" :placeholder="$t('autoPush.condPhName')"></el-input>
      </el-form-item>
      <el-form-item :label="$t('autoPush.condLabelDesc')">
        <el-input v-model="form.description" type="textarea" :rows="2" :placeholder="$t('autoPush.condPhDesc')"></el-input>
      </el-form-item>

      <!-- 条件规则 -->
      <el-form-item :label="$t('autoPush.condRulesLabel')">
        <!-- v-for 放在 template 上：提示行要作为每条规则的兄弟节点（不能塞进 flex 的 rule-row） -->
        <template v-for="(rule, index) in form.rules">
          <div :key="index" class="rule-row">
            <el-select v-model="rule.field" :placeholder="$t('autoPush.condField')" style="width: 150px;" @change="handleFieldChange(rule)">
              <el-option
                v-for="(labelKey, field) in fieldOptions"
                :key="field"
                :label="$t('autoPush.' + labelKey)"
                :value="field"
              ></el-option>
            </el-select>
            <el-select v-model="rule.operator" :placeholder="$t('autoPush.condOperator')" style="width: 120px; margin: 0 10px;" @change="handleOperatorChange(rule)">
              <el-option
                v-for="(labelKey, operator) in operatorOptions"
                :key="operator"
                :label="$t('autoPush.' + labelKey)"
                :value="operator"
              ></el-option>
            </el-select>
            <!-- 值：枚举字段用下拉（固定候选值，避免手打英文原始值出错），
                 其余字段（数字/天数/次数）保持输入框；
                 配合「包含 / 不包含」（IN / NOT IN）时支持多选 -->
            <el-select
              v-if="isEnumField(rule.field)"
              v-model="rule.value"
              :multiple="isMultiValueOperator(rule.operator)"
              collapse-tags
              :placeholder="$t('autoPush.condValue')"
              style="width: 190px;"
            >
              <el-option
                v-for="opt in enumOptions(rule.field)"
                :key="opt.value"
                :label="$t('autoPush.' + opt.labelKey)"
                :value="opt.value"
              ></el-option>
            </el-select>
            <el-input v-else v-model="rule.value" :placeholder="$t('autoPush.condValue')" style="width: 150px;"></el-input>
            <el-button type="text" style="color: #F56C6C; margin-left: 10px;" @click="removeRule(index)">{{ $t('autoPush.condRemoveRule') }}</el-button>
          </div>
          <!-- 踩坑提示（2026-10-03）：从未录音的用户「距上次录音天数」为空，
               任何比较都不命中 —— 而打卡提醒最该覆盖的恰恰是这些人 -->
          <div
            v-if="rule.field === 'daysSinceLastVj'"
            :key="'vj-tip-' + index"
            class="rule-note"
          >{{ $t('autoPush.condVjDaysTip') }}</div>
          <!-- 含从未录音的版本：把「从没录过」当 999 天，说明一下这个口径 -->
          <div
            v-else-if="rule.field === 'vjDaysSinceLastOrNever'"
            :key="'vj-orn-ever-tip-' + index"
            class="rule-note rule-note--info"
          >{{ $t('autoPush.condVjDaysOrNeverTip') }}</div>
        </template>
        <el-button type="text" @click="addRule">+ {{ $t('autoPush.condAddRule') }}</el-button>
      </el-form-item>

      <el-form-item :label="$t('autoPush.condMatchLabel')" prop="matchMode">
        <el-radio-group v-model="form.matchMode">
          <el-radio label="AND">{{ $t('autoPush.condMatchAndOpt') }}</el-radio>
          <el-radio label="OR">{{ $t('autoPush.condMatchOrOpt') }}</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 优先级已移除（2026-09-20）：条件回归纯人群筛选器后它不影响任何推送行为，
           只用于列表排序、且语义容易让人误以为「高优先级条件会覆盖其他条件」 -->
      <el-form-item :label="$t('autoPush.condStatusLabel')">
        <el-select v-model="form.status" :placeholder="$t('autoPush.condStatusPh')">
          <el-option :label="$t('autoPush.condStatusActive')" value="active"></el-option>
          <el-option :label="$t('autoPush.condStatusInactive')" value="inactive"></el-option>
        </el-select>
      </el-form-item>

      <!-- 关联内容已移除（2026-08-29）：条件只负责「筛选哪些人」，
           推送内容统一在推送任务里配置 -->

      <el-form-item>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">{{ $t('autoPush.condSave') }}</el-button>
        <el-button @click="$emit('cancel')">{{ $t('autoPush.condCancel') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { getCondition, saveCondition, updateCondition } from '@/api/autoPush'
// 字段/操作符/枚举值的定义：与条件列表页、任务页的条件摘要共用一份
import { FIELD_LABEL_KEYS, OPERATOR_LABEL_KEYS, isEnumField as isEnumFieldType, enumValueOptions } from './condition-fields'
// 前后端格式转换与快捷模板：与任务页的条件对话框共用一份
import {
  CONDITION_QUICK_TEMPLATES,
  buildSubmitRules,
  isMultiValueOperator,
  matchAllOf,
  normalizeRuleForForm
} from './condition-form'

/**
 * 条件表单本体（条件管理页与推送任务页对话框共用）
 *
 * 保存成功后只 emit('saved', { id, name })，由外层决定「返回上一页」还是「关弹窗并选中」。
 * 单个入口各自维护跳转/关闭逻辑，表单逻辑（含转换）只有这一份。
 *
 * @props conditionId 为空 = 新建；有值 = 编辑该条件
 * @emits saved  payload: { id, name }
 * @emits cancel
 */
export default {
  name: 'ConditionForm',
  props: {
    conditionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      form: {
        name: '',
        description: '',
        rules: [{ field: '', operator: '', value: '' }],
        matchMode: 'AND',
        status: 'active'
      },
      formRules: {
        name: [{ required: true, message: this.$t('autoPush.condRuleName'), trigger: 'blur' }],
        matchMode: [{ required: true, message: this.$t('autoPush.condRuleMatch'), trigger: 'change' }]
      },
      // 字段与操作符下拉：遍历共享定义，避免模板里再抄一份 el-option 列表
      fieldOptions: FIELD_LABEL_KEYS,
      operatorOptions: OPERATOR_LABEL_KEYS,
      quickTemplates: CONDITION_QUICK_TEMPLATES,
      submitLoading: false
    }
  },
  computed: {
    isEdit() {
      return !!this.conditionId
    }
  },
  created() {
    if (this.conditionId) {
      this.loadDetail(this.conditionId)
    }
  },
  methods: {
    // 该字段是否为枚举字段（决定「值」用下拉还是输入框）
    isEnumField(field) {
      return isEnumFieldType(field)
    },
    // 枚举字段的候选值列表
    enumOptions(field) {
      return enumValueOptions(field)
    },
    // 「包含 / 不包含」对应后端的 IN / NOT IN，支持多值（以逗号分隔存储）
    isMultiValueOperator(operator) {
      return isMultiValueOperator(operator)
    },
    // 字段切换：值域完全变了，清空旧值避免残留（例如从「性别」切到「年龄」）
    handleFieldChange(rule) {
      rule.value = this.isMultiValueOperator(rule.operator) ? [] : ''
    },
    // 操作符在「单值 ⇄ 多值」之间切换时同步值的类型：
    // 数组喂给输入框会显示成 "1,2"，字符串喂给多选会按字符拆开
    handleOperatorChange(rule) {
      const shouldBeArray = this.isMultiValueOperator(rule.operator)
      if (shouldBeArray && !Array.isArray(rule.value)) {
        rule.value = rule.value
          ? String(rule.value).split(',').map(s => s.trim()).filter(Boolean)
          : []
      } else if (!shouldBeArray && Array.isArray(rule.value)) {
        rule.value = rule.value.length ? rule.value[0] : ''
      }
    },
    // 快捷模板：预填名称与规则（名称只在为空时填，避免覆盖运营已输入的内容）
    applyTemplate(tpl) {
      this.form.rules = tpl.rules.map(rule => ({ ...rule }))
      if (!this.form.name) {
        this.form.name = this.$t('autoPush.' + tpl.nameKey)
      }
      if (this.$refs.conditionForm) {
        this.$refs.conditionForm.clearValidate()
      }
    },
    // 加载条件详情
    loadDetail(id) {
      getCondition(id).then(res => {
        const data = res.data || {}
        const rules = (data.rules && data.rules.length)
          ? data.rules.map(normalizeRuleForForm)
          : [{ field: '', operator: '', value: '' }]
        this.form = {
          id: data.id,
          name: data.name || '',
          description: data.description || '',
          rules,
          matchMode: data.matchMode || 'AND',
          status: data.status || 'active'
        }
      }).catch(() => {
        this.$message.error(this.$t('autoPush.condDetailLoadFail'))
      })
    },
    // 添加条件行
    addRule() {
      this.form.rules.push({ field: '', operator: '', value: '' })
    },
    // 移除条件行
    removeRule(index) {
      if (this.form.rules.length <= 1) {
        this.$message.warning(this.$t('autoPush.condWarnOne'))
        return
      }
      this.form.rules.splice(index, 1)
    },
    // 提交表单
    handleSubmit() {
      this.$refs.conditionForm.validate(valid => {
        if (!valid) return
        const submitRules = buildSubmitRules(this.form.rules)
        if (submitRules.length === 0) {
          this.$message.warning(this.$t('autoPush.condWarnRule'))
          return
        }
        this.submitLoading = true
        const submitData = {
          ...this.form,
          rules: submitRules,
          match_all: matchAllOf(this.form.matchMode)
        }
        // 移除前端专用字段，避免后端兼容层产生歧义
        delete submitData.matchMode
        const api = this.isEdit ? updateCondition(submitData.id, submitData) : saveCondition(submitData)
        api.then(res => {
          this.$message.success(this.isEdit ? this.$t('autoPush.condUpdateSuccess') : this.$t('autoPush.condCreateSuccess'))
          const data = (res && res.data) || {}
          // 后端创建接口返回 { id, name }；更新接口可能不回传，用表单值兜底
          this.$emit('saved', {
            id: this.isEdit ? this.form.id : data.id,
            name: data.name || this.form.name
          })
        }).catch(() => {
          this.$message.error(this.$t('autoPush.condSaveFail'))
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>

<style scoped>
.rule-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
/* 规则下方的踩坑提示（如「距上次录音天数」覆盖不到从未录音的人） */
.rule-note {
  margin: -4px 0 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #E6A23C;
}
/* 口径说明（不是警告，用中性灰） */
.rule-note--info {
  color: #909399;
}
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.tpl-btn {
  margin: 0 8px 4px 0;
}
</style>
